import { LocationProduct, StockWarehouse, StockWarehousePayload } from 'types/models/StockReceipt';
import axiosInstance from './axios';
import PaginationData from 'types/models/PaginationData';
import joinURLParams from 'utils/joinUrlParams';
import { parseVenue } from './venuesServices';
import { parseUser } from './authServices';

const WAREHOUSES_URL = 'stock/warehouses';

export const getStockWarehouses = async (
  page?: number,
  searchTerm?: string,
  managerUserId?: string,
  venueId?: string,
): Promise<PaginationData<StockWarehouse>> => {
  const url = joinURLParams({ page, searchTerm, managerUserId, venueId }, WAREHOUSES_URL);
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return {
      currentPage: data.current_page,
      data: parseWarehouses(data.data),
      perPage: data.per_page,
      total: data.total,
    };
  } catch (error) {
    throw error;
  }
};

export const getVenueWarehouses = async (
  venueId: string,
  extraParams?: Record<string, string>,
): Promise<StockWarehouse[]> => {
  let url = `${WAREHOUSES_URL}?venueId=${venueId}&isActive=true&simple=true`;
  if (extraParams) {
    url = joinURLParams(extraParams, url);
  }

  try {
    const { data } = await axiosInstance({
      method: 'get',
      url,
    });
    return parseWarehouses(data);
  } catch (error) {
    throw error;
  }
};

export const getStockWarehouse = async (id: string): Promise<StockWarehouse> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `${WAREHOUSES_URL}/${id}`,
    });
    return parseWarehouse(data);
  } catch (error) {
    throw error;
  }
};

export const getWarehouseProducts = async (warehouseId: string): Promise<LocationProduct[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `${WAREHOUSES_URL}/${warehouseId}/products`,
    });
    return parseLocationProducts(data);
  } catch (error) {
    throw error;
  }
};

export const getCommerceProductsAvailable = async (commerceId: string): Promise<LocationProduct[]> => {
  try {
    const { data } = await axiosInstance({
      method: 'get',
      url: `stock/commerces/${commerceId}/products`,
    });
    return parseLocationProducts(data);
  } catch (error) {
    throw error;
  }
};

export const storeStockWarehouse = async (payload: StockWarehousePayload): Promise<StockWarehouse> => {
  try {
    const { data } = await axiosInstance({
      method: 'post',
      url: WAREHOUSES_URL,
      data: payload,
    });
    return parseWarehouse(data);
  } catch (error) {
    throw error;
  }
};

export const updateStockWarehouse = async (id: string, payload: StockWarehousePayload): Promise<StockWarehouse> => {
  try {
    const { data } = await axiosInstance({
      method: 'put',
      url: `${WAREHOUSES_URL}/${id}`,
      data: payload,
    });
    return parseWarehouse(data);
  } catch (error) {
    throw error;
  }
};

const parseWarehouses = (data: any[]): StockWarehouse[] => {
  return data.map(parseWarehouse);
};

export const parseWarehouse = (item: any): StockWarehouse => {
  return {
    id: item.id,
    name: item.name,
    type: item.type,
    isActive: item.is_active,
    venueId: item.venue_id,
    managerUserId: item.manager_user_id,
    venue: item.venue ? parseVenue(item.venue) : undefined,
    editor: item.editor ? parseUser(item.editor) : undefined,
  };
};

const parseLocationProducts = (data: any[]): LocationProduct[] => {
  return data.map(parseLocationProduct);
};

const parseLocationProduct = (item: any): LocationProduct => {
  return {
    commerceProductId: item.commerce_product_id,
    globalProductId: item.global_product_id,
    sku: item.sku,
    name: item.name,
    type: item.type,
    stock: item.stock,
  };
};
